import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Languages, ListChecks } from 'lucide-react';
import type { NpsSurvey } from '@/shared/types/nps';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { useNpsUsers, useSurvey } from '../api/use-nps';
import { DuplicateSurveyError, duplicateSurvey, sortedQuestions } from '../model/duplicate-survey';
import { PassbookIdField } from '../components/survey/passbook-id-field';
import { SurveyOwnerSelect } from '../components/survey/survey-owner-select';
import { useNpsAdmin } from '../hooks/use-nps-access';

interface Props {
  /** Pesquisa de origem; `null` fecha o dialog. */
  surveyId: string | null;
  onClose: () => void;
  onDuplicated: (id: string) => void;
}

function hasTranslations(survey: NpsSurvey): boolean {
  const filled = (i18n: object | null | undefined) => Boolean(i18n && Object.keys(i18n).length > 0);
  return filled(survey.i18n) || (survey.questions ?? []).some((q) => filled(q.i18n));
}

/**
 * Copia pesquisa + perguntas (com traduções e opções) para a mesma conta ou,
 * no master, para a conta NPS de outra loja — o Passbook é da conta.
 */
export function SurveyDuplicateDialog({ surveyId, onClose, onDuplicated }: Props) {
  const queryClient = useQueryClient();
  const open = Boolean(surveyId);
  const { isAdmin } = useNpsAdmin();
  const { data: source, isLoading } = useSurvey(surveyId ?? undefined);
  const { data: npsUsers = [] } = useNpsUsers({ enabled: open && isAdmin });

  const [title, setTitle] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [progress, setProgress] = useState<{ copied: number; total: number } | null>(null);
  const running = progress !== null;

  const owner = npsUsers.find((u) => u.id === ownerId);
  const sourceOwnerId = source?.user_id ?? source?.user?.id ?? '';
  const ownerPassbook =
    owner?.legacy_user_id ??
    (ownerId && ownerId === sourceOwnerId ? source?.user?.legacy_user_id ?? '' : '');
  const questionCount = source?.questions?.length ?? 0;

  useEffect(() => {
    if (!open || !source) return;
    setTitle(source.title);
    setOwnerId(source.user_id ?? source.user?.id ?? '');
    setProgress(null);
  }, [open, source]);

  const submit = async () => {
    if (!source) return;
    if (!title.trim()) {
      toast.error('Informe o título da cópia.');
      return;
    }
    if (isAdmin && !ownerId) {
      toast.error('Selecione o cliente NPS que vai receber a cópia.');
      return;
    }

    setProgress({ copied: 0, total: sortedQuestions(source).length });
    try {
      const copy = await duplicateSurvey(
        source,
        { title, ownerId: isAdmin ? ownerId : undefined },
        (copied, total) => setProgress({ copied, total }),
      );
      toast.success(
        questionCount === 1 ? 'Pesquisa duplicada com 1 pergunta.' : `Pesquisa duplicada com ${questionCount} perguntas.`,
      );
      queryClient.invalidateQueries({ queryKey: ['nps', 'surveys'] });
      onDuplicated(copy.id);
    } catch (err) {
      if (err instanceof DuplicateSurveyError && err.surveyId) {
        toast.error(`${err.message}. ${err.copiedQuestions} de ${questionCount} perguntas copiadas — confira a cópia.`, {
          duration: 10_000,
        });
        queryClient.invalidateQueries({ queryKey: ['nps', 'surveys'] });
        onDuplicated(err.surveyId);
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Falha ao duplicar a pesquisa.');
    } finally {
      setProgress(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !running) onClose();
      }}
    >
      <DialogContent className="max-w-xl" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Duplicar pesquisa</DialogTitle>
          <DialogDescription>
            Copia as perguntas, as traduções e as regras. A cópia começa como rascunho e com o próprio link e QR Code.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !source ? (
          <div className="space-y-3">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-10 rounded-lg" />
          </div>
        ) : (
          <form
            id="survey-duplicate-form"
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-sm">
              <span className="flex items-center gap-1.5">
                <ListChecks className="size-4 text-primary" />
                {questionCount === 1 ? '1 pergunta' : `${questionCount} perguntas`}
              </span>
              {hasTranslations(source) ? (
                <span className="flex items-center gap-1.5">
                  <Languages className="size-4 text-primary" />
                  Com traduções
                </span>
              ) : null}
              <span className="text-muted-foreground">
                {source.response_type === 'IDENTIFIED' ? 'Identificada' : 'Anônima'}
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duplicate-title">Título da cópia</Label>
              <Input
                id="duplicate-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={running}
              />
            </div>

            {isAdmin ? (
              <section className="space-y-4 border-t border-border/60 pt-5">
                <h3 className="text-sm font-semibold">Destino</h3>
                <SurveyOwnerSelect
                  users={npsUsers}
                  value={ownerId}
                  onChange={setOwnerId}
                  disabled={running}
                  hint="Para outra loja, escolha a conta NPS dela: o Passbook fica na conta."
                />
                <PassbookIdField
                  id="duplicate-passbook"
                  value={ownerPassbook}
                  onChange={() => {}}
                  disabled
                  warnBonusWithoutPassbook={Boolean(source.answer_bonus)}
                />
              </section>
            ) : null}
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
            Cancelar
          </Button>
          <Button type="submit" form="survey-duplicate-form" loading={running} disabled={!source}>
            <Copy className="size-4" />
            {progress
              ? progress.total > 0
                ? `Copiando ${progress.copied} de ${progress.total}…`
                : 'Copiando…'
              : 'Duplicar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
